// Original synthesised telephone cues. No external audio or copyrighted recordings.
// One active call cue per page. Never queue a ring to play after its call ends.
let activeStop = null;
export const CALL_CUES = {
  incoming: { frequencies: [620, 845, 1240, 1690], wave: 'sine', tremoloHz: 25, period: 3, pulses: [[0, 0.4], [0.6, 0.4]], cycles: 12, volume: 0.025 },
  ringback: { frequencies: [400, 450], wave: 'sine', period: 3, pulses: [[0, 0.4], [0.6, 0.4]], cycles: 12, volume: 0.055 },
  busy: { frequencies: [400], wave: 'sine', period: 0.75, pulses: [[0, 0.375]], cycles: 3, volume: 0.06 },
};
export function startCallTone(kind) {
  activeStop?.();
  const spec = CALL_CUES[kind];
  const AC = typeof window !== 'undefined' && (window.AudioContext || window.webkitAudioContext);
  if (!spec || !AC) return () => {};
  let ctx;
  try { ctx = new AC(); } catch { return () => {}; }
  let stopped = false;
  let timer = null;
  const nodes = [];
  const stop = () => {
    if (stopped) return;
    stopped = true;
    clearTimeout(timer);
    nodes.forEach(node => {
      try { node.stop?.(); } catch { /* already stopped */ }
      try { node.disconnect(); } catch { /* unavailable */ }
    });
    try { Promise.resolve(ctx.close()).catch(() => {}); } catch { /* already closed */ }
    if (activeStop === stop) activeStop = null;
  };
  activeStop = stop;
  const schedule = () => {
    if (stopped || ctx.state !== 'running') return;
    const gain = ctx.createGain();
    nodes.push(gain);
    gain.connect(ctx.destination);
    const now = ctx.currentTime;
    gain.gain.setValueAtTime(0, now);
    for (let cycle = 0; cycle < spec.cycles; cycle += 1) {
      spec.pulses.forEach(([offset, duration]) => {
        const t = now + cycle * spec.period + offset;
        gain.gain.setValueAtTime(0, t);
        gain.gain.linearRampToValueAtTime(spec.volume, t + 0.012);
        gain.gain.setValueAtTime(spec.volume, t + duration - 0.025);
        gain.gain.linearRampToValueAtTime(0, t + duration);
      });
    }
    // Inharmonic bell partials + rapid striker flutter evoke a mechanical
    // 1980s telephone. Keep modulation before the cadence gate so pauses
    // stay completely silent, rather than allowing the LFO to leak sound.
    let voiceOutput = gain;
    if (spec.tremoloHz) {
      const tremolo = ctx.createGain();
      const depth = ctx.createGain();
      const striker = ctx.createOscillator();
      nodes.push(tremolo, depth, striker);
      tremolo.gain.setValueAtTime(0.65, now);
      depth.gain.setValueAtTime(0.35, now);
      striker.frequency.setValueAtTime(spec.tremoloHz, now);
      striker.connect(depth);
      depth.connect(tremolo.gain);
      tremolo.connect(gain);
      striker.start(now);
      striker.stop(now + spec.period * spec.cycles);
      voiceOutput = tremolo;
    }
    spec.frequencies.forEach(hz => {
      const osc = ctx.createOscillator();
      nodes.push(osc);
      osc.type = spec.wave;
      osc.frequency.setValueAtTime(hz, now);
      osc.connect(voiceOutput);
      osc.start(now);
      osc.stop(now + spec.period * spec.cycles);
    });
    clearTimeout(timer);
    timer = setTimeout(stop, spec.period * spec.cycles * 1000 + 50);
  };
  // If unlock resolves after cancellation, schedule() checks stopped first.
  try {
    if (ctx.state === 'suspended') {
      Promise.resolve(ctx.resume()).then(schedule).catch(stop);
    } else schedule();
  } catch { stop(); }
  // Close contexts even if a browser never settles its resume promise.
  if (!timer) timer = setTimeout(stop, spec.period * spec.cycles * 1000 + 1000);
  return stop;
}
